import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { showWarningToast } from 'lib/ToastHelper';
import { useCallback, useEffect } from 'react';
import { Col, Badge } from 'reactstrap';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import { getExportCsvUrlParams } from './helper';
import {
  waListingDataAtom,
  checkedListAtom,
  vulnCountForCheckedHostsAtom,
  forceRerenderUIAtom,
  checkedHostsVulnMapAtom,
  orderbyAtom,
} from './state';
import { appliedWAIdsAtom } from '../state';
import { useWAListFilterValue } from '../hooks';
import { companyIdAtom } from 'views/state';

const DashboardActions = () => {
  const listingData = useAtomValue(waListingDataAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(checkedListAtom);
  const [appliedRowIds, setAppliedRowIds] = useAtom(appliedWAIdsAtom);
  const [vulnMap, setVulnMap] = useAtom(checkedHostsVulnMapAtom);
  const forceRerenderUI = useSetAtom(forceRerenderUIAtom);
  const vulnCountForCheckedHosts = useAtomValue(vulnCountForCheckedHostsAtom);
  const searchPhrase = useWAListFilterValue<string>('search');
  const selectedWebAppsFilter = useWAListFilterValue<
    'active' | 'inactive' | 'deleted'
  >('filter');
  const orderBy = useAtomValue(orderbyAtom);
  const companyId = useAtomValue(companyIdAtom);
  const selectionChanged = (): boolean => {
    return appliedRowIds.sort().join(',') !== selectedRowIds.sort().join(',');
  };

  const markAppliedRows = useCallback(
    (rowIds) => {
      if (listingData) {
        // TODO: update the applied row background color to indicate the applied rows
        for (let i = 0; i < listingData.items?.length; i++) {
          const row = listingData.items[i];
          row['isAppliedForDashboard'] = false;
        }
        const effectedRows = listingData?.items?.filter((row) =>
          rowIds.includes(row.id),
        );
        if (effectedRows?.length > 0) {
          effectedRows.forEach((row) => {
            row['isAppliedForDashboard'] = true;
          });
        }
        forceRerenderUI(Math.random().toString());
      }
    },
    [forceRerenderUI, listingData],
  );

  useEffect(() => {
    markAppliedRows(appliedRowIds);
  }, [appliedRowIds, listingData, markAppliedRows]);

  useEffect(() => {
    if (listingData?.items) {
      const newVulnMap = { ...vulnMap };

      // Remove vulnerability counts for unchecked hosts
      Object.keys(newVulnMap).forEach((id) => {
        if (!selectedRowIds.includes(Number(id))) {
          delete newVulnMap[id];
        }
      });

      // Add vulnerability counts for newly checked hosts
      listingData.items.forEach((host) => {
        if (selectedRowIds.includes(host.id)) {
          newVulnMap[host.id] = host.vuln || 0;
        }
      });

      setVulnMap(newVulnMap);
    }
  }, [selectedRowIds, listingData, setVulnMap]);

  const onUpdateDashboardClick = () => {
    // check if the vulnerability count for any of the selected host is greater then zero
    // This is to avoid plotting the dashboard graphs for all selected hosts with zero vulnerability count
    // for details check: https://securepoint360.atlassian.net/browse/SB-1722, https://securepoint360.atlassian.net/browse/SB-1723
    if (vulnCountForCheckedHosts > 0 || selectedRowIds?.length === 0) {
      markAppliedRows(selectedRowIds);
      setAppliedRowIds(selectedRowIds);
    } else {
      showWarningToast(
        'Dashboard can not be applied, as for the selected hosts, the vulnerabilities/bugs total count is zero. Please try a different combination of hosts.',
      );
    }
  };

  return (
    <Col
      className="text-right"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
      }}
    >
      {selectedRowIds?.length > 0 && (
        <Col md="auto" className="text-left text-nowrap">
          {selectedRowIds?.length > 0 && (
            <>
              ( Selected:{' '}
              <label
                className="label btn"
                style={{
                  backgroundColor: 'rgba(7, 114, 219, 0.1)',
                  color: 'black',
                }}
              >
                {selectedRowIds.length}
              </label>
              )
            </>
          )}
        </Col>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {selectedRowIds?.length > 0 && (
          <>
            <IconButton
              id="clearSelectionButton"
              iconClass="fas fa-times"
              tooltip="Clear all selections"
              onClick={() => setSelectedRowIds([])}
            />
            <div
              style={{
                width: '1px',
                height: '24px',
                backgroundColor: '#dee2e6',
                margin: '0 5px',
              }}
            />
          </>
        )}
        <IconButton
          id="updateButton"
          iconClass="fa fa-chart-line fa-lg"
          tooltip="Update Dashboard For Selected Hosts"
          disabled={
            !selectionChanged() ||
            (selectedRowIds.length === 0 && appliedRowIds.length === 0)
          }
          active={appliedRowIds.length > 0}
          stale={selectionChanged()}
          onClick={onUpdateDashboardClick}
        />
        <div style={{ position: 'relative' }} className="d-inline">
          <IconButton
            id="resetButton"
            iconClass="fa fa-undo fa-lg"
            tooltip={
              appliedRowIds?.length > 0 ? (
                <>
                  Reset dashboard to show all hosts
                  <br />
                  Applied ( {appliedRowIds.length} hosts )
                </>
              ) : (
                'Reset dashboard to show all hosts'
              )
            }
            disabled={appliedRowIds.length === 0}
            onClick={() => {
              markAppliedRows([]);
              setAppliedRowIds([]);
            }}
          />
          {appliedRowIds?.length > 0 && (
            <Badge
              color="transparent"
              pill
              style={{
                position: 'absolute',
                top: '-7px',
                right: '-6px',
                fontSize: '0.70rem',
                color: '#fff',
                textShadow:
                  '-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black',
                cursor: 'pointer',
              }}
            >
              {appliedRowIds.length}
            </Badge>
          )}
        </div>
        <FileDownloaderButton
          buttonId="was_export_all"
          iconClass="fa fa-file-export"
          tooltip="Export CSV data for filtered Web Application"
          fileName={`${localStorage
            .getItem('currentCompany')
            ?.toLocaleLowerCase()}_web_applications.csv`}
          method="GET"
          downloadUrl="web_apps/export-csv"
          urlParams={getExportCsvUrlParams(
            companyId,
            orderBy,
            selectedWebAppsFilter,
            searchPhrase,
            selectedRowIds,
          )}
          onSuccessCallback={() => showSuccessToast('File download completed.')}
          onFailureCallback={(message) => showFailureToast(message)}
        />
      </div>
    </Col>
  );
};

export default DashboardActions;
